// ─────────────────────────────────────────────────────────────────────────────
// body_graphics.ts — Per-page decorative body fills
//
// "Graphics" here = decorative fills layered UNDER the writing surface
// (mosaics, watermarks, alhambra-style tilings, …) PLUS structured
// page templates (diary entry / agenda / weekly / monthly calendar).
// Templates live here because they're visual page-frames the user
// writes onto, not constraints on the writing geometry.
//
// Drawn beneath justification, so writing guides stay crisp on top.
//
// Adding a graphic = one REGISTRY entry; no renderer change required.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';

export interface BodyGraphic {
  id: string;
  label: string;
  draw(ctx: CanvasRenderingContext2D, layout: PageLayout): void;
}

function contentBox(L: PageLayout): { x: number; y: number; w: number; h: number } {
  return {
    x: L.x + L.marginLeft,
    y: L.y + L.marginTop,
    w: L.width - L.marginLeft - L.marginRight,
    h: L.height - L.marginTop - L.marginBottom,
  };
}

// ── Built-ins: decorative fills ─────────────────────────────────────────────

const NONE: BodyGraphic = {
  id: 'none',
  label: 'None',
  draw: () => { /* nothing */ },
};

/** Centred faint watermark sigil — concentric circles with rune cross. */
const WATERMARK: BodyGraphic = {
  id: 'watermark',
  label: 'Watermark sigil',
  draw(ctx, L) {
    const b = contentBox(L);
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const R  = Math.min(b.w, b.h) * 0.28;
    ctx.save();
    ctx.globalAlpha = 0.10;
    ctx.strokeStyle = '#3a2010';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.arc(cx, cy, R * 0.78, 0, Math.PI * 2);
    ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy);
    ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R);
    ctx.stroke();
    // Cardinal pip dots
    ctx.fillStyle = '#3a2010';
    for (const a of [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2]) {
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * R, cy + Math.sin(a) * R, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  },
};

/** Scattered star-pip constellation — soft dots + a few thin connections. */
const CONSTELLATIONS: BodyGraphic = {
  id: 'constellations',
  label: 'Constellations',
  draw(ctx, L) {
    const b = contentBox(L);
    // Seeded PRNG so the pattern is stable per draw
    let s = 0x9e3779b1;
    const rng = () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) % 10000) / 10000; };
    const stars: Array<{ x: number; y: number; r: number }> = [];
    for (let i = 0; i < 36; i++) {
      stars.push({
        x: b.x + rng() * b.w,
        y: b.y + rng() * b.h,
        r: 0.6 + rng() * 1.4,
      });
    }
    ctx.save();
    ctx.fillStyle = 'rgba(58, 32, 16, 0.32)';
    for (const st of stars) {
      ctx.beginPath();
      ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
      ctx.fill();
    }
    // A handful of thin connecting lines between nearest neighbours
    ctx.strokeStyle = 'rgba(58, 32, 16, 0.10)';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    for (let i = 0; i < stars.length; i += 3) {
      const a = stars[i];
      let nearest = stars[(i + 1) % stars.length];
      let bestD = Infinity;
      for (const c of stars) {
        if (c === a) continue;
        const dx = c.x - a.x, dy = c.y - a.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; nearest = c; }
      }
      ctx.moveTo(a.x, a.y); ctx.lineTo(nearest.x, nearest.y);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** Alhambra-style mosaic — interlocking eight-point stars on a grid. */
const MOSAIC: BodyGraphic = {
  id: 'mosaic',
  label: 'Mosaic (alhambra)',
  draw(ctx, L) {
    const b = contentBox(L);
    const tile = 36;
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = '#3a2010';
    ctx.lineWidth = 0.45;
    for (let gy = b.y; gy < b.y + b.h; gy += tile) {
      for (let gx = b.x; gx < b.x + b.w; gx += tile) {
        const cx = gx + tile / 2;
        const cy = gy + tile / 2;
        const r1 = tile * 0.42;
        const r2 = tile * 0.22;
        ctx.beginPath();
        for (let k = 0; k < 8; k++) {
          const a1 = (k / 8) * Math.PI * 2;
          const a2 = ((k + 0.5) / 8) * Math.PI * 2;
          const x1 = cx + Math.cos(a1) * r1;
          const y1 = cy + Math.sin(a1) * r1;
          const x2 = cx + Math.cos(a2) * r2;
          const y2 = cy + Math.sin(a2) * r2;
          if (k === 0) ctx.moveTo(x1, y1); else ctx.lineTo(x1, y1);
          ctx.lineTo(x2, y2);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
    ctx.restore();
  },
};

/** Compass rose tucked into the top-right corner. */
const COMPASS_CORNER: BodyGraphic = {
  id: 'compass-corner',
  label: 'Compass corner',
  draw(ctx, L) {
    const b = contentBox(L);
    const cx = b.x + b.w - 70;
    const cy = b.y + 70;
    const R = 50;
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = '#3a2010';
    ctx.fillStyle = '#3a2010';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.arc(cx, cy, R * 0.6, 0, Math.PI * 2);
    ctx.stroke();
    // Four-point star
    for (let k = 0; k < 4; k++) {
      const a = k * Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a - 0.18) * R * 0.55, cy + Math.sin(a - 0.18) * R * 0.55);
      ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
      ctx.lineTo(cx + Math.cos(a + 0.18) * R * 0.55, cy + Math.sin(a + 0.18) * R * 0.55);
      ctx.closePath();
      ctx.fill();
    }
    // Cardinal letters (faint)
    ctx.globalAlpha = 0.18;
    ctx.font = 'italic 9px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', cx, cy - R - 8);
    ctx.fillText('S', cx, cy + R + 8);
    ctx.fillText('E', cx + R + 8, cy);
    ctx.fillText('W', cx - R - 8, cy);
    ctx.restore();
  },
};

// ── Built-ins: templates ────────────────────────────────────────────────────

/** Diary entry — Date header at top + two stacked writing fields. */
const TEMPLATE_DIARY: BodyGraphic = {
  id: 'tpl-diary',
  label: 'Template — diary entry',
  draw(ctx, L) {
    const b = contentBox(L);
    const headerH = 36;
    const dateLineY = b.y + headerH - 10;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.42)';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(b.x, dateLineY);
    ctx.lineTo(b.x + 220, dateLineY);
    ctx.stroke();
    ctx.fillStyle = 'rgba(58, 32, 16, 0.5)';
    ctx.font = 'italic 10px Georgia, serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Date', b.x, dateLineY - 2);
    // Two writing fields — "morning" and "evening" with subtle dividers
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.25)';
    ctx.lineWidth = 0.45;
    const fieldTop = b.y + headerH + 8;
    const mid = b.y + (b.h + headerH) / 2 + 6;
    // Section labels
    ctx.font = 'italic 9px Georgia, serif';
    ctx.fillText('morning', b.x, fieldTop - 4);
    ctx.fillText('evening', b.x, mid + 14);
    // Faint ruled lines in each field
    const spacing = 26;
    ctx.beginPath();
    for (let ly = fieldTop + spacing; ly < mid - 4; ly += spacing) {
      ctx.moveTo(b.x, ly); ctx.lineTo(b.x + b.w, ly);
    }
    for (let ly = mid + 24 + spacing; ly < b.y + b.h; ly += spacing) {
      ctx.moveTo(b.x, ly); ctx.lineTo(b.x + b.w, ly);
    }
    ctx.stroke();
    // Mid divider
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.32)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(b.x, mid); ctx.lineTo(b.x + b.w, mid);
    ctx.stroke();
    ctx.restore();
  },
};

/** Agenda — time column on the left, lines next to each slot. */
const TEMPLATE_AGENDA: BodyGraphic = {
  id: 'tpl-agenda',
  label: 'Template — agenda',
  draw(ctx, L) {
    const b = contentBox(L);
    const colW = 56;
    const slots = 14;
    const startHour = 7;
    const slotH = b.h / slots;
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.30)';
    ctx.lineWidth = 0.5;
    ctx.fillStyle = 'rgba(58, 32, 16, 0.55)';
    ctx.font = '10px Georgia, serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < slots; i++) {
      const ly = b.y + i * slotH;
      ctx.beginPath();
      ctx.moveTo(b.x + colW, ly + slotH);
      ctx.lineTo(b.x + b.w, ly + slotH);
      ctx.stroke();
      const hour = ((startHour + i) % 24).toString().padStart(2, '0');
      ctx.fillText(`${hour}:00`, b.x + colW - 6, ly + slotH / 2);
    }
    // Vertical divider after the time column
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.4)';
    ctx.lineWidth = 0.55;
    ctx.beginPath();
    ctx.moveTo(b.x + colW, b.y);
    ctx.lineTo(b.x + colW, b.y + b.h);
    ctx.stroke();
    ctx.restore();
  },
};

/** Weekly calendar — 7 columns labelled Mon..Sun. */
const TEMPLATE_WEEKLY: BodyGraphic = {
  id: 'tpl-weekly',
  label: 'Template — week view',
  draw(ctx, L) {
    const b = contentBox(L);
    const headerH = 22;
    const cols = 7;
    const colW = b.w / cols;
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    ctx.save();
    // Column dividers
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.3)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let i = 1; i < cols; i++) {
      ctx.moveTo(b.x + i * colW, b.y);
      ctx.lineTo(b.x + i * colW, b.y + b.h);
    }
    ctx.stroke();
    // Header underline
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.42)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y + headerH);
    ctx.lineTo(b.x + b.w, b.y + headerH);
    ctx.stroke();
    // Labels
    ctx.fillStyle = 'rgba(58, 32, 16, 0.65)';
    ctx.font = 'italic 11px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < cols; i++) {
      ctx.fillText(labels[i], b.x + (i + 0.5) * colW, b.y + headerH / 2);
    }
    // Faint horizontal stripes inside cells
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.15)';
    ctx.lineWidth = 0.4;
    const stripeSpacing = 28;
    ctx.beginPath();
    for (let ly = b.y + headerH + stripeSpacing; ly < b.y + b.h; ly += stripeSpacing) {
      ctx.moveTo(b.x, ly); ctx.lineTo(b.x + b.w, ly);
    }
    ctx.stroke();
    ctx.restore();
  },
};

/** Monthly calendar — 6 weeks × 7 days grid. */
const TEMPLATE_MONTHLY: BodyGraphic = {
  id: 'tpl-monthly',
  label: 'Template — month view',
  draw(ctx, L) {
    const b = contentBox(L);
    const headerH = 22;
    const cols = 7;
    const rows = 6;
    const colW = b.w / cols;
    const rowH = (b.h - headerH) / rows;
    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    ctx.save();
    // Vertical lines
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.3)';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (let i = 1; i < cols; i++) {
      ctx.moveTo(b.x + i * colW, b.y);
      ctx.lineTo(b.x + i * colW, b.y + b.h);
    }
    // Horizontal lines
    for (let i = 1; i <= rows; i++) {
      const ly = b.y + headerH + i * rowH;
      ctx.moveTo(b.x, ly); ctx.lineTo(b.x + b.w, ly);
    }
    ctx.stroke();
    // Header underline (heavier)
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.45)';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y + headerH);
    ctx.lineTo(b.x + b.w, b.y + headerH);
    ctx.stroke();
    // Labels
    ctx.fillStyle = 'rgba(58, 32, 16, 0.65)';
    ctx.font = 'italic 10px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < cols; i++) {
      ctx.fillText(labels[i], b.x + (i + 0.5) * colW, b.y + headerH / 2);
    }
    // Tiny "date number" position hint in top-left of each cell
    ctx.font = 'italic 9px Georgia, serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(58, 32, 16, 0.25)';
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        ctx.fillText('—', b.x + c * colW + 4, b.y + headerH + r * rowH + 3);
      }
    }
    ctx.restore();
  },
};

// ── Registry ────────────────────────────────────────────────────────────────

const REGISTRY: Map<string, BodyGraphic> = new Map();
function register(d: BodyGraphic): void { REGISTRY.set(d.id, d); }

register(NONE);
// Decorative
register(WATERMARK);
register(CONSTELLATIONS);
register(MOSAIC);
register(COMPASS_CORNER);
// Templates
register(TEMPLATE_DIARY);
register(TEMPLATE_AGENDA);
register(TEMPLATE_WEEKLY);
register(TEMPLATE_MONTHLY);

export function getBodyGraphic(id: string | undefined | null): BodyGraphic {
  if (!id) return NONE;
  return REGISTRY.get(id) ?? NONE;
}

export function listBodyGraphics(): BodyGraphic[] {
  return [...REGISTRY.values()];
}
